import { Navigate, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Box, Drawer, ListItemButton, ListItemIcon, ListItemText, useMediaQuery, useTheme } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { useCallback, useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import {
  analysisRunner,
  estimateAnalysisSecondsPerGame,
  formatAnalysisDuration,
  useAnalysisDataStore,
  useAnalysisStore,
} from "@/features/analysis";
import { importRunner, useBackgroundImport, useGamesStore } from "@/features/games";
import { usePatternsStore } from "@/features/patterns";
import { useProfileStore } from "@/features/profile";
import { nikolaRunSessions } from "@/features/chat/messaging/use-nikola-run";
import { useAuth } from "@/app/context/AuthContext";
import { ChessNewsMarquee } from "@/features/news";
import { useNotification } from "@/app/context/NotificationContext";
import {
  AppContentFrame,
  AppMobileNavigation,
  ChesslaBrand,
  DesktopNavigationList,
  DRAWER_WIDTH,
  SidebarStatusSurfaces,
  titleForPath,
} from "./shell";
import { useEntitlementsStore } from "@/features/entitlements/entitlements.store";
import { NikolaCreditsLink } from "@/features/entitlements/NikolaCreditsLink";
import { ProductTour } from "@/features/product-tour";
import { useUserSettingsStore } from "@/features/preferences/user-settings.store";

export const shouldUseNativeScroll = (
  prefersReducedMotion: boolean,
  animationsEnabled = true,
  tourActive = false,
): boolean => prefersReducedMotion || !animationsEnabled || tourActive;

export const observeLenisSize = (targets: readonly Element[], resize: () => void): (() => void) => {
  if (typeof ResizeObserver === "undefined") return () => undefined;
  const observer = new ResizeObserver(resize);
  new Set(targets).forEach((target) => observer.observe(target));
  return () => observer.disconnect();
};

export const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, onboardingCompleted, isAuthenticated, isLoading: isAuthLoading, userId } = useAuth();
  const { showNotification } = useNotification();
  const gamesOwnerUserId = useGamesStore((s) => s.ownerUserId);
  const availableCredits = useEntitlementsStore((s) => s.snapshot?.credits.available ?? null);
  const currentOwner = isAuthenticated && gamesOwnerUserId === userId;
  const storedIsAnalyzing = useAnalysisStore((s) => s.isRunning);
  const storedGamesCompleted = useAnalysisStore((s) => s.gamesCompleted);
  const storedGamesTotal = useAnalysisStore((s) => s.gamesTotal);
  const storedJobs = useAnalysisStore((s) => s.jobs);
  const storedProgressByGameId = useAnalysisStore((s) => s.progressByGameId);
  const storedImportProgress = useGamesStore((s) => s.importProgress);
  const isAnalyzing = currentOwner && storedIsAnalyzing;
  const gamesCompleted = currentOwner ? storedGamesCompleted : 0;
  const gamesTotal = currentOwner ? storedGamesTotal : 0;
  const jobs = currentOwner ? storedJobs : [];
  const progressByGameId = currentOwner ? storedProgressByGameId : {};
  const importProgress = currentOwner ? storedImportProgress : null;
  const mainRef = useRef<HTMLElement | null>(null);
  const previousPathnameRef = useRef(location.pathname);
  const [mainElement, setMainElement] = useState<HTMLElement | null>(null);
  const [tourActive, setTourActive] = useState(false);
  const animationsEnabled = useUserSettingsStore((state) => state.settings.animationsEnabled);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  useEffect(() => {
    const ownerUserId = isAuthLoading || !isAuthenticated ? null : userId;
    nikolaRunSessions.setOwner(ownerUserId);
    const previousOwner = useGamesStore.getState().ownerUserId;
    if (previousOwner && previousOwner !== ownerUserId) {
      analysisRunner.stop();
      importRunner.stop();
      useAnalysisStore.getState().clearSession();
    }
    useGamesStore.getState().setOwner(ownerUserId);
    usePatternsStore.getState().setOwner(ownerUserId);
    useAnalysisDataStore.getState().setOwner(ownerUserId);
  }, [isAuthLoading, isAuthenticated, userId]);

  // Silent catch-up import every 30 minutes; it only speaks up when it found new games. Analysis stays manual.
  useBackgroundImport(!isAuthLoading && isAuthenticated && onboardingCompleted ? userId : null, (count) =>
    showNotification(
      `Imported ${count} new ${count === 1 ? "game" : "games"} from Chess.com. Analyze them from Games when you're ready.`,
    ),
  );

  useEffect(() => {
    if (isAuthLoading || !isAuthenticated) return;

    void useProfileStore
      .getState()
      .loadProfile(userId)
      .catch(() => undefined);
    if (!onboardingCompleted) return;

    void useGamesStore
      .getState()
      .loadGames()
      .catch(() => undefined);
    void useAnalysisDataStore
      .getState()
      .loadUnanalyzed()
      .catch(() => undefined);
  }, [isAuthLoading, isAuthenticated, onboardingCompleted, userId]);

  const currentJob = jobs.find((job) => job.status === "analyzing");
  const currentProgress = currentJob ? progressByGameId[currentJob.gameId] : null;
  const remainingAnalysisMs = (() => {
    const queuedMs = jobs
      .filter((job) => job.status === "queued")
      .reduce((total, job) => total + estimateAnalysisSecondsPerGame(job.preset) * 1_000, 0);

    if (!currentJob) return queuedMs;
    if (!currentProgress?.startTime || currentProgress.currentMove === 0) {
      return queuedMs + estimateAnalysisSecondsPerGame(currentJob.preset) * 1_000;
    }

    const elapsedMs = Date.now() - currentProgress.startTime;
    const remainingMoves = Math.max(0, currentProgress.totalMoves - currentProgress.currentMove);
    return queuedMs + (elapsedMs / currentProgress.currentMove) * remainingMoves;
  })();
  const overallProgress = currentProgress
    ? ((gamesCompleted + currentProgress.currentMove / currentProgress.totalMoves) / gamesTotal) * 100
    : gamesTotal > 0
      ? (gamesCompleted / gamesTotal) * 100
      : 0;

  useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    if (isAnalyzing && previousPathname === "/analyze" && location.pathname !== "/analyze") {
      showNotification("Nikola keeps analyzing in the background.");
    }
    previousPathnameRef.current = location.pathname;
  }, [isAnalyzing, location.pathname, showNotification]);

  useEffect(() => {
    if (isAnalyzing) {
      if (currentProgress && gamesTotal > 0) {
        document.title = `CHESSLA: Analyzing ${gamesCompleted + 1}/${gamesTotal}, ${currentProgress.currentMove}/${currentProgress.totalMoves} moves`;
        return;
      }
      document.title = `CHESSLA: Preparing ${gamesTotal} game${gamesTotal === 1 ? "" : "s"}`;
      return;
    }

    const routeTitle = titleForPath(location.pathname);
    document.title = routeTitle === "CHESSLA" ? routeTitle : `CHESSLA: ${routeTitle}`;
  }, [currentProgress, gamesCompleted, gamesTotal, isAnalyzing, location.pathname]);

  // Profile remains the only available destination until the user has linked
  // an account and completed the first analysis in onboarding.
  const isOnboardingComplete = onboardingCompleted;
  const setMainRef = useCallback((element: HTMLElement | null) => {
    mainRef.current = element;
    setMainElement(element);
  }, []);

  useEffect(() => {
    const lenisInstances = new Map<HTMLElement, Lenis>();
    const nestedCleanups = new Map<HTMLElement, () => void>();
    const resizeCleanups = new Map<HTMLElement, () => void>();

    // Initialize Lenis on the main wrapper
    const wrapper = mainRef.current;
    if (shouldUseNativeScroll(prefersReducedMotion, animationsEnabled, tourActive)) return;
    if (wrapper) {
      const content = wrapper.firstElementChild as HTMLElement | null;
      const lenis = new Lenis({
        wrapper,
        content: content ?? wrapper,
        smoothWheel: true,
        // A lower interpolation keeps the main page glide smooth without
        // changing the tighter, row-snapping behavior inside game tables.
        lerp: 0.11,
      });
      lenisInstances.set(wrapper, lenis);
      resizeCleanups.set(
        wrapper,
        observeLenisSize([wrapper, content ?? wrapper], () => lenis.resize()),
      );
    }

    const configureTableRowSettling = (tableScroller: HTMLElement, lenis: Lenis) => {
      let settleTimer: number | undefined;
      let previousScroll = lenis.scroll;
      let direction = 0;
      let isSettling = false;
      let settleReleaseTimer: number | undefined;

      const settleToCompleteRow = () => {
        if (lenis.isScrolling) {
          settleTimer = window.setTimeout(settleToCompleteRow, 100);
          return;
        }
        if (direction === 0 || isSettling) return;

        const rows = Array.from(tableScroller.querySelectorAll<HTMLElement>("tbody tr, [data-lenis-row]"));
        if (rows.length === 0) return;
        const scrollTop = lenis.scroll;
        const viewportBottom = scrollTop + tableScroller.clientHeight;
        const scrollerRect = tableScroller.getBoundingClientRect();
        const rowTop = (row: HTMLElement) => row.getBoundingClientRect().top - scrollerRect.top + scrollTop;
        const target =
          direction > 0
            ? rows.find((row) => rowTop(row) + row.offsetHeight >= viewportBottom - 2)
            : rows.find((row) => rowTop(row) < scrollTop - 2 && rowTop(row) + row.offsetHeight > scrollTop + 2);
        if (!target) return;

        const targetScroll =
          direction > 0
            ? Math.min(lenis.limit, rowTop(target) + target.offsetHeight - tableScroller.clientHeight)
            : Math.max(0, rowTop(target));
        if (Math.abs(targetScroll - scrollTop) < 2) return;

        isSettling = true;
        if (settleReleaseTimer != null) window.clearTimeout(settleReleaseTimer);
        lenis.scrollTo(targetScroll, {
          duration: 0.72,
          easing: (progress) => 1 - (1 - progress) ** 3,
          onComplete: () => {
            isSettling = false;
            if (settleReleaseTimer != null) window.clearTimeout(settleReleaseTimer);
          },
        });
        settleReleaseTimer = window.setTimeout(() => {
          isSettling = false;
        }, 1_000);
        requestLenisFrame();
      };

      const unsubscribe = lenis.on("scroll", (instance) => {
        if (isSettling) return;
        const delta = instance.scroll - previousScroll;
        previousScroll = instance.scroll;
        if (Math.abs(delta) < 0.1) return;
        direction = delta > 0 ? 1 : -1;
        if (settleTimer != null) window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(settleToCompleteRow, 140);
      });

      return () => {
        unsubscribe();
        if (settleTimer != null) window.clearTimeout(settleTimer);
        if (settleReleaseTimer != null) window.clearTimeout(settleReleaseTimer);
      };
    };

    // Initialize Lenis on nested scrollable elements (message list, move list, etc.)
    const initNestedLenis = () => {
      const nestedScrollContainers = wrapper?.querySelectorAll("[data-lenis-scroll]");
      nestedScrollContainers?.forEach((el) => {
        if (el !== wrapper && !lenisInstances.has(el as HTMLElement)) {
          const nestedWrapper = el as HTMLElement;
          const nestedContent = nestedWrapper.firstElementChild as HTMLElement | null;
          const lenis = new Lenis({
            wrapper: nestedWrapper,
            content: nestedContent ?? nestedWrapper,
            smoothWheel: true,
            ...(nestedWrapper.hasAttribute("data-lenis-table")
              ? {
                  lerp: 0.05,
                  wheelMultiplier: 0.55,
                }
              : {}),
          });
          lenisInstances.set(nestedWrapper, lenis);
          resizeCleanups.set(
            nestedWrapper,
            observeLenisSize([nestedWrapper, nestedContent ?? nestedWrapper], () => lenis.resize()),
          );
          if (nestedWrapper.hasAttribute("data-lenis-table")) {
            nestedCleanups.set(nestedWrapper, configureTableRowSettling(nestedWrapper, lenis));
          }
        }
      });
    };

    const destroyRemovedNestedLenis = () => {
      if (!wrapper) return;
      lenisInstances.forEach((lenis, element) => {
        if (element !== wrapper && !wrapper.contains(element)) {
          nestedCleanups.get(element)?.();
          nestedCleanups.delete(element);
          resizeCleanups.get(element)?.();
          resizeCleanups.delete(element);
          lenis.destroy();
          lenisInstances.delete(element);
        }
      });
    };

    // Run immediately in case children are already rendered
    initNestedLenis();

    // Only rescan when a mutation actually adds a nested scroll container.
    // Virtualized lists mount/unmount rows while scrolling; rescanning the
    // entire page for every one of those mutations defeats virtualization.
    const observer = new MutationObserver((records) => {
      destroyRemovedNestedLenis();
      const addedNestedScroller = records.some((record) =>
        Array.from(record.addedNodes).some(
          (node) =>
            node instanceof Element &&
            (node.matches("[data-lenis-scroll]") || node.querySelector("[data-lenis-scroll]")),
        ),
      );
      if (addedNestedScroller) initNestedLenis();
    });
    if (wrapper) {
      observer.observe(wrapper, { childList: true, subtree: true });
    }

    // Run animation frames only while Lenis is actively smoothing movement.
    // A permanent RAF loop competes with Stockfish even when the page is idle.
    let rafId: number | null = null;
    let keepAliveUntil = 0;
    const raf = (time: number) => {
      lenisInstances.forEach((lenis) => lenis.raf(time));
      if (Array.from(lenisInstances.values()).some((lenis) => lenis.isScrolling) || time < keepAliveUntil) {
        rafId = requestAnimationFrame(raf);
      } else {
        rafId = null;
      }
    };
    const requestLenisFrame = () => {
      // Keep Lenis alive briefly between wheel events. Trackpads often pause
      // between momentum samples; stopping immediately creates a visible jump
      // when the next sample wakes the animation loop.
      // Trackpad momentum and virtualized row measurement can pause between
      // samples; keep Lenis warm through that gap instead of restarting a
      // cold RAF loop and producing a visible hitch.
      keepAliveUntil = performance.now() + 800;
      if (rafId != null) return;
      // The RAF loop is intentionally dormant while idle so it does not
      // compete with Stockfish. Prime Lenis's clock when waking it; otherwise
      // its first frame receives the entire idle period as deltaTime and
      // visibly jumps toward the new target before smoothing subsequent
      // wheel ticks.
      const now = performance.now();
      lenisInstances.forEach((lenis) => {
        lenis.time = now;
      });
      rafId = requestAnimationFrame(raf);
    };
    wrapper?.addEventListener("wheel", requestLenisFrame, { passive: true });
    wrapper?.addEventListener("touchmove", requestLenisFrame, { passive: true });
    wrapper?.addEventListener("scroll", requestLenisFrame, { passive: true, capture: true });

    return () => {
      observer.disconnect();
      wrapper?.removeEventListener("wheel", requestLenisFrame);
      wrapper?.removeEventListener("touchmove", requestLenisFrame);
      wrapper?.removeEventListener("scroll", requestLenisFrame, true);
      if (rafId != null) cancelAnimationFrame(rafId);
      nestedCleanups.forEach((cleanup) => cleanup());
      nestedCleanups.clear();
      resizeCleanups.forEach((cleanup) => cleanup());
      resizeCleanups.clear();
      lenisInstances.forEach((lenis) => lenis.destroy());
      lenisInstances.clear();
    };
  }, [prefersReducedMotion, animationsEnabled, tourActive]);

  const handleLogout = async () => {
    await logout();
    void navigate("/login");
  };

  if (!isOnboardingComplete && location.pathname !== "/profile" && location.pathname !== "/about") {
    return <Navigate to="/profile" replace />;
  }

  return (
    <Box
      sx={{
        "--mobile-app-bar-height": "44px",
        display: "flex",
        width: "100%",
        height: "100dvh",
        minWidth: 0,
        overflow: "hidden",
        border: { xs: "none", md: "2px solid" },
        borderColor: "primary.main",
        boxSizing: "border-box",
        pl: { xs: "env(safe-area-inset-left)", md: 0 },
        pr: { xs: "env(safe-area-inset-right)", md: 0 },
      }}
    >
      {/* Sidebar — only on md+ */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              bgcolor: "background.default",
              borderRight: "2px solid",
              borderColor: "primary.main",
              borderRadius: 0,
              boxSizing: "border-box",
              // Only the navigation list scrolls; brand, status, news and logout stay put.
              overflow: "hidden",
              '& > :not([data-testid="sidebar-navigation"])': { flexShrink: 0 },
            },
          }}
        >
          <Box sx={{ borderBottom: "2px solid", borderColor: "primary.main" }}>
            <ListItemButton
              component={NavLink}
              to="/about"
              disableRipple
              sx={{
                p: 2,
                minHeight: 56,
                borderRadius: 0,
                color: "primary.main",
                transition: "none",
                "&:hover": { bgcolor: "rgba(240,236,226,0.05)" },
                "&.active": {
                  bgcolor: "primary.main",
                  color: "background.default",
                },
                "&.active:hover": { bgcolor: "primary.main" },
              }}
            >
              <ChesslaBrand />
            </ListItemButton>
          </Box>

          <DesktopNavigationList onboardingCompleted={isOnboardingComplete} isAnalyzing={isAnalyzing} />
          <NikolaCreditsLink available={availableCredits} />

          <SidebarStatusSurfaces
            isAnalyzing={isAnalyzing}
            currentJob={currentJob}
            currentProgress={currentProgress}
            gamesCompleted={gamesCompleted}
            gamesTotal={gamesTotal}
            overallProgress={overallProgress}
            remainingLabel={formatAnalysisDuration(remainingAnalysisMs, true)}
            queuedCount={jobs.filter((job) => job.status === "queued").length}
            importProgress={importProgress}
            onStopAnalysis={() => analysisRunner.stop()}
            onStopImport={() => importRunner.stop()}
          />

          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <ChessNewsMarquee />
          </Box>

          <Box sx={{ borderTop: "2px solid", borderColor: "primary.main" }}>
            <ListItemButton
              onClick={() => void handleLogout()}
              sx={{
                minHeight: 56,
                p: 2,
                borderRadius: 0,
                transition: "none",
                "&:hover": {
                  bgcolor: "secondary.main",
                  color: "background.default",
                  "& .MuiListItemIcon-root": {
                    color: "background.default",
                  },
                  "& .MuiListItemText-primary": {
                    color: "background.default",
                  },
                },
              }}
            >
              <ListItemIcon sx={{ color: "secondary.main", minWidth: 36 }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{
                  variant: "body2",
                  fontWeight: 700,
                  letterSpacing: "0.03em",
                  textTransform: "uppercase",
                  color: "secondary.main",
                }}
              />
            </ListItemButton>
          </Box>
        </Drawer>
      )}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          "& > main": {
            pt: { xs: "var(--mobile-app-bar-height, 44px)", md: 0 },
          },
        }}
      >
        <AppContentFrame mainElement={mainElement} onMainElementChange={setMainRef} />
      </Box>

      {isMobile && (
        <AppMobileNavigation
          pathname={location.pathname}
          onboardingCompleted={isOnboardingComplete}
          isAnalyzing={isAnalyzing}
          onLogout={() => void handleLogout()}
        />
      )}
      <ProductTour onActiveChange={setTourActive} />
    </Box>
  );
};
